import {
  ConditionalFormattingState,
  DiffType,
  GetCssClassNamesOption,
  RepresentationData,
} from '@ardoq/data-model';
import {
  GraphItem,
  getGraphItemMetadata,
  type GraphItemMetadata,
} from './GraphItem';
import SparkMD5 from 'spark-md5';
import {
  GraphItemFormattingMaps,
  ItemLabels,
  RawGraphNode2,
  SVGColorAttributes,
  URLFieldValue,
} from '../types';
import { ComponentGraphNodeModel } from './types';
import { classes } from '@ardoq/common-helpers';
import {
  DEFAULT_EMPTY_ITEM_LABELS,
  DIFF_TYPE,
  LI_BACKGROUND_CLASS_NAME,
  VOID_REPRESENTATION_DATA,
} from '@ardoq/global-consts';
import { GraphInterface } from '../graphInterface/graphInterface';
import { RepresentationInterface } from '../representationInterface/representationInterface';

export type GraphNodeMetadata = GraphItemMetadata & {
  representationData: RepresentationData | null;
  isGhost: boolean;
  isContext: boolean;
  visualDiffType: DiffType;
  itemLabels: ItemLabels;
  urlFieldValues: URLFieldValue[] | null;
  cssClassNames: string;
  color: SVGColorAttributes;
  conditionalFormattingState: ConditionalFormattingState;
};

type GetComponentGraphNodeMetadataArgs = GraphItemFormattingMaps & {
  viewInterface: GraphInterface & RepresentationInterface;
  rawGraphItem: RawGraphNode2;
  urlFieldValuesByComponentId: Map<string, URLFieldValue[] | null>;
  representationDataByComponentId: Map<string, RepresentationData | null>;
  nodeLabelsData: Map<string, ItemLabels>;
  isContext: boolean;
};
export const getComponentGraphNodeMetadata = ({
  viewInterface,
  rawGraphItem,
  urlFieldValuesByComponentId,
  representationDataByComponentId,
  nodeColorsByGraphItemId,
  conditionalFormattingState,
  nodeLabelsData,
  isContext,
}: GetComponentGraphNodeMetadataArgs): GraphNodeMetadata | null => {
  const graphItemMetadata = getGraphItemMetadata({
    viewInterface,
    rawGraphItem,
  });
  const modelId = rawGraphItem.modelId;
  if (!modelId) {
    return null;
  }

  const urlFieldValues = urlFieldValuesByComponentId.get(modelId) ?? null;
  const itemLabels =
    nodeLabelsData.get(rawGraphItem.id) ?? DEFAULT_EMPTY_ITEM_LABELS;

  return {
    ...graphItemMetadata,
    cssClassNames: viewInterface.getComponentCssClassNames(modelId) ?? '',
    visualDiffType:
      viewInterface.getComponentAttribute(modelId, DIFF_TYPE) ?? DiffType.NONE,
    urlFieldValues,
    itemLabels,
    representationData: graphItemMetadata.isComponent
      ? (representationDataByComponentId.get(modelId) ??
        VOID_REPRESENTATION_DATA)
      : null,
    color: nodeColorsByGraphItemId.get(rawGraphItem.id) ?? {},
    conditionalFormattingState:
      conditionalFormattingState.get(rawGraphItem.id) ??
      ConditionalFormattingState.NORMAL,
    isContext,
  };
};

type GraphNodeConstructorArgs = {
  modelId: string;
  id?: string;
  metadata: GraphNodeMetadata;
};
export class ComponentGraphNode
  extends GraphItem
  implements ComponentGraphNodeModel
{
  static create(modelId: string, id: string, metadata: GraphNodeMetadata) {
    return new ComponentGraphNode({
      modelId,
      id,
      metadata,
    });
  }

  protected metadata: GraphNodeMetadata;
  public readonly isGhost: boolean;

  private constructor({ modelId, id, metadata }: GraphNodeConstructorArgs) {
    super(modelId, metadata, id);
    this.metadata = metadata;

    this.hash = SparkMD5.hash(this.getRawHash());
    this.isGhost = metadata.isGhost;
  }
  override getLabel() {
    // legacy compatibility
    return this.label || this.metadata.itemLabels.mainLabel || '';
  }
  override getVisualDiffType(): DiffType {
    return this.metadata.visualDiffType;
  }
  override getCSS(options?: GetCssClassNamesOption): string {
    if (!options?.useAsBackgroundStyle) {
      return this.metadata.cssClassNames;
    }
    return classes(this.metadata.cssClassNames, LI_BACKGROUND_CLASS_NAME);
  }
  override hasURLFields(): boolean {
    return Boolean(this.metadata.urlFieldValues?.length);
  }
  override getItemLabels(): ItemLabels {
    return this.metadata.itemLabels;
  }
  override setItemLabels(itemLabels: ItemLabels): void {
    this.metadata.itemLabels = itemLabels;
  }

  getUrlFieldValues() {
    return this.metadata.urlFieldValues;
  }
  setUrlFieldValues(urlFieldValues: URLFieldValue[] | null) {
    this.metadata.urlFieldValues = urlFieldValues;
  }

  getConditionalFormattingState(): ConditionalFormattingState {
    return this.metadata.conditionalFormattingState;
  }

  setConditionalFormattingState(
    conditionalFormattingState: ConditionalFormattingState
  ): void {
    this.metadata.conditionalFormattingState = conditionalFormattingState;
  }

  setMetadata(metadata: GraphNodeMetadata) {
    this.metadata = metadata;
  }
  getMetadata() {
    return this.metadata;
  }

  getImage() {
    const representationData = this.metadata.representationData;

    if (!representationData || !representationData.isImage) {
      return '';
    }

    return this.metadata.representationData?.value ?? '';
  }

  getShape() {
    return this.metadata.representationData?.shapeName ?? 'predefinedProcess';
  }

  getRepresentationData() {
    return this.metadata.representationData;
  }
  setRepresentationData(representationData: RepresentationData | null) {
    this.metadata.representationData = representationData;
  }

  getCssColorAttributes() {
    return this.metadata.color;
  }
  setCssColorAttributes(color: SVGColorAttributes) {
    this.metadata.color = color;
  }

  getIsContext() {
    return this.metadata.isContext;
  }
  setIsContext(isContext: boolean) {
    this.metadata.isContext = isContext;
  }

  protected getRawHash() {
    return `${super.getRawHash()}${this.getImage()}${this.getShape()}`;
  }
}
